import { useData } from "../../runtime/provider";
import Favourites from "@components/Home/Favourites";
import ForYou from "@components/Home/ForYou";
import PopularPlaylists from "@components/Home/PopularPlaylists";
import RecommendedArtists from "@components/Home/RecommendedArtists";
import LBox from "@components/LBox";
import LFlatList from "@components/LFlatList";
import LScreen from "@components/LScreen";
import LScrollView from "@components/LScrollView";
import LSuspense from "@components/LSuspense";
import Loading from "@components/LSuspense/Loading";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { PrebidPOC } from "@components/PrebidPOC/PrebidPOC";
import BrowseGenres from "@components/Search/Browse";
import {
  SearchTextBox,
  type SearchTextBoxRef,
} from "@components/Search/SearchBox";
import SearchResult, {
  SearchHistoryResult,
} from "@components/Search/SearchResult";
import {
  useRecentSearchesPaginated,
  useSearchMutations,
} from "@hooks/trpc/useSearch";
import { useSearch as useSearchAll } from "@hooks/useSearchAll";
import { theme } from "@theme";
import { keyExtractor } from "@utils/data";
import { captureException } from "@utils/captureException";
import {
  type ImageGroupKey,
  ImageGroupKeyProvider,
  setActiveImageGroup,
} from "@utils/imageRequestGroups";

import { usePrewarmSongs } from "@utils/player";
import { selectSearchSongsForPrewarm } from "./searchPrewarmSelection";
import { useAuth } from "@utils/firebaseAuth/auth";
import type { Search } from "@libs/wire";
import * as Haptics from "expo-haptics";
import { useFocusEffect, useNavigation } from "expo-router";
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  FlatList,
  Keyboard,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import Animated, {
  Easing,
  FadeInDown,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { SentryScreenDisplay } from "@components/SentryScreenDisplay";

const EXPLORE_CONTENT_KEY: ImageGroupKey = ["route", "explore", "content"];
const EXPLORE_RECENT_SEARCHES_KEY: ImageGroupKey = [
  "route",
  "explore",
  "recent-searches",
];
const EXPLORE_SEARCH_RESULTS_KEY: ImageGroupKey = [
  "route",
  "explore",
  "search-results",
];
const SEARCH_CANCEL_GAP = theme.spacing.s3;
const SEARCH_CANCEL_WIDTH = 58;
const SEARCH_CANCEL_ANIMATION_DURATION_MS = 350;
// Duration-based spring: eases out gently instead of the flat feel of a
// plain timing curve, with dampingRatio 1 so the input width never overshoots.
const SEARCH_CANCEL_SPRING_CONFIG = {
  duration: SEARCH_CANCEL_ANIMATION_DURATION_MS,
  dampingRatio: 1,
};
const SEARCH_OVERLAY_FADE_IN_MS = 200;
const SEARCH_OVERLAY_FADE_OUT_MS = 250;
// Last row starts at 6×25=150ms; with the 250ms per-item fade the whole
// cascade resolves in ~400ms.
const SEARCH_ITEM_STAGGER_MS = 25;
// Only stagger the rows visible on first paint — rows mounted later while
// scrolling should appear without an artificial delay.
const SEARCH_ITEM_STAGGER_MAX_INDEX = 6;

const triggerSearchCancelHaptic = () => {
  if (Platform.OS === "web") {
    return;
  }

  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch((error) => {
    captureException(error, {
      channel: "breadcrumb",
      area: "haptics",
      operation: "search-cancel",
    });
  });
};

const ExploreContentBody = ({ onReady }: { onReady: () => void }) => {
  useEffect(onReady, [onReady]);

  return (
    <LScrollView
      showsVerticalScrollIndicator={false}
      style={styles.exploreScroll}
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
    >
      <LBox mb="s4">
        <Favourites />
      </LBox>
      <PrebidPOC />
      <LBox mb="m2">
        <ForYou />
      </LBox>
      <LBox mb="m2">
        <RecommendedArtists />
      </LBox>
      <LBox mb="m2">
        <PopularPlaylists />
      </LBox>
      <LBox px="s4">
        <BrowseGenres />
      </LBox>
    </LScrollView>
  );
};

const ExploreContent = React.memo(function ExploreContent({
  onReady,
}: {
  onReady: () => void;
}) {
  return (
    <ImageGroupKeyProvider groupKey={EXPLORE_CONTENT_KEY}>
      <LSuspense fallback={<Loading />}>
        <ExploreContentBody onReady={onReady} />
      </LSuspense>
    </ImageGroupKeyProvider>
  );
});

type SearchHeaderProps = {
  inputRef: React.Ref<SearchTextBoxRef>;
  onBlur: () => void;
  onCancel: () => void;
  onChange: (value: string) => void;
  onClear: () => void;
  onFocus: () => void;
  onSubmitEditing: () => void;
  showCancel: boolean;
  value: string;
};

const SearchHeader = ({
  inputRef,
  onBlur,
  onCancel,
  onChange,
  onClear,
  onFocus,
  onSubmitEditing,
  showCancel,
  value,
}: SearchHeaderProps) => {
  // Single source of truth for the open/close transition, driven by the
  // showCancel prop so the reverse animation is guaranteed to run on cancel.
  const cancelProgress = useSharedValue(0);

  useEffect(() => {
    cancelProgress.set(
      withSpring(showCancel ? 1 : 0, SEARCH_CANCEL_SPRING_CONFIG),
    );
  }, [cancelProgress, showCancel]);

  const cancelContainerAnimatedStyle = useAnimatedStyle(
    () => ({
      width: cancelProgress.value * (SEARCH_CANCEL_WIDTH + SEARCH_CANCEL_GAP),
      opacity: cancelProgress.value,
    }),
    [cancelProgress],
  );

  const cancelLabelAnimatedStyle = useAnimatedStyle(
    () => ({
      transform: [
        {
          translateX: (1 - cancelProgress.value) * SEARCH_CANCEL_GAP,
        },
      ],
    }),
    [cancelProgress],
  );

  return (
    <LBox pb="s3" pt="s3" px="s4" flexDirection="row" alignItems="center">
      <View style={styles.searchInput}>
        <SearchTextBox
          ref={inputRef}
          value={value}
          onChange={onChange}
          placeholder="Search"
          onFocus={onFocus}
          onClear={onClear}
          onSubmitEditing={onSubmitEditing}
          onBlur={onBlur}
        />
      </View>
      <Animated.View
        pointerEvents={showCancel ? "auto" : "none"}
        style={[styles.cancelContainer, cancelContainerAnimatedStyle]}
      >
        <Animated.View
          style={[styles.cancelLabelContainer, cancelLabelAnimatedStyle]}
        >
          <LPressable
            sentryEventName="search_cancel_button"
            shouldDismissKeyboard={false}
            onPress={onCancel}
            style={styles.cancelPressable}
            fadeEnabled
            hitSlop={10}
          >
            <LText color="white" variant="bodyM" numberOfLines={1}>
              Cancel
            </LText>
          </LPressable>
        </Animated.View>
      </Animated.View>
    </LBox>
  );
};

type RecentSearchesPaneProps = {
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onAddSearch: (item: Pick<Search, "id" | "type">) => void;
  onRemoveSearch: (id: string) => void;
  recentSearches: Search[];
  userId: string | undefined;
};

const RecentSearchesPane = ({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onAddSearch,
  onRemoveSearch,
  recentSearches,
  userId,
}: RecentSearchesPaneProps) => (
  <ImageGroupKeyProvider groupKey={EXPLORE_RECENT_SEARCHES_KEY}>
    <LBox px="s4" flex={1}>
      {isLoading ? (
        <ActivityIndicator size="large" />
      ) : recentSearches.length ? (
        <LFlatList
          showsVerticalScrollIndicator={false}
          keyboardDismissMode="none"
          keyboardShouldPersistTaps="handled"
          data={recentSearches}
          keyExtractor={keyExtractor}
          // One keyboard-up screenful in a single mount commit: batched
          // mounts (the old 7 + 4×N) land mid-animation as separate Fabric
          // transactions and restart the stagger while the spring is running.
          initialNumToRender={12}
          maxToRenderPerBatch={8}
          updateCellsBatchingPeriod={80}
          windowSize={3}
          onEndReached={() => {
            if (!hasNextPage || isFetchingNextPage) {
              return;
            }

            fetchNextPage().catch((error) => {
              captureException(error, {
                channel: "breadcrumb",
                area: "search",
                operation: "fetch-next-recent-searches-page",
              });
            });
          }}
          onEndReachedThreshold={0.4}
          ListHeaderComponent={
            <LText color="white" fontWeight="bold" variant="bodyL" mb="s2">
              Recent searches
            </LText>
          }
          ListFooterComponent={
            isFetchingNextPage ? <ActivityIndicator size="small" /> : null
          }
          renderItem={({ item: search, index }) => (
            <Animated.View
              entering={FadeInDown.duration(250).delay(
                Math.min(index, SEARCH_ITEM_STAGGER_MAX_INDEX) *
                  SEARCH_ITEM_STAGGER_MS,
              )}
            >
              <SearchHistoryResult
                item={search}
                userId={userId}
                onAddSearch={onAddSearch}
                onRemoveSearch={onRemoveSearch}
              />
            </Animated.View>
          )}
        />
      ) : null}
    </LBox>
  </ImageGroupKeyProvider>
);

type SearchResultsPaneProps = {
  flatListRef: React.Ref<FlatList>;
  isSearching: boolean;
  onNavigateAway: () => void;
  onSelectResult: (result: Search, position: number) => void;
  searchResults: Search[];
};

const SearchResultsPane = ({
  flatListRef,
  isSearching,
  onNavigateAway,
  onSelectResult,
  searchResults,
}: SearchResultsPaneProps) => {
  // Search is THE surface a song can be reached from without ever going
  // through an album/artist/playlist screen's own read-path prewarm — by
  // the time a user finishes scanning results and taps (~1.5-3s after the
  // list settles), materialization has had a head start, so the play-time
  // poll gate's first tick is far more likely to hit 200 immediately.
  // `searchResults` is a single bounded response (no infinite scroll), so
  // this is already "the rendered page of results" — no separate
  // viewability tracking needed.
  const prewarmSongs = useMemo(
    () => selectSearchSongsForPrewarm(searchResults),
    [searchResults],
  );
  usePrewarmSongs(prewarmSongs);

  return (
    <ImageGroupKeyProvider groupKey={EXPLORE_SEARCH_RESULTS_KEY}>
      <LBox px="s4" flex={1}>
        <LFlatList
          style={styles.searchResultsList}
          data={searchResults}
          keyExtractor={keyExtractor}
          initialNumToRender={1}
          maxToRenderPerBatch={1}
          updateCellsBatchingPeriod={80}
          windowSize={2}
          renderItem={({ item, index }) => (
            <SearchResult
              item={item}
              key={item.id}
              onNavigateAway={onNavigateAway}
              onSelect={() => onSelectResult(item, index)}
            />
          )}
          ListEmptyComponent={
            isSearching ? (
              <ActivityIndicator size="large" />
            ) : (
              <LBox alignItems="center" justifyContent="center" mt="m1">
                <LText variant="bodyMBold" mb="m1">
                  No results
                </LText>
              </LBox>
            )
          }
          ref={flatListRef}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
        />
      </LBox>
    </ImageGroupKeyProvider>
  );
};

export const ExploreScreen = () => {
  const session = useData();
  const {
    isSearching,
    recordSearchSelection,
    searchResults,
    searchTerm,
    setSearchTermInput,
  } = useSearchAll(session.utils.catalog.searchAnything, "mobile_explore");
  const {
    query: {
      data: recentSearchesPages,
      fetchNextPage: fetchNextRecentSearchesPage,
      hasNextPage: hasNextRecentSearchesPage,
      isFetchingNextPage: isFetchingNextRecentSearchesPage,
      isLoading: isRecentSearchesLoading,
    },
  } = useRecentSearchesPaginated(20);
  const { firebaseUser } = useAuth();
  const { addSearch, removeSearch } = useSearchMutations();
  const navigation = useNavigation();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isExploreContentReady, setIsExploreContentReady] = useState(false);
  const markExploreContentReady = useCallback(
    () => setIsExploreContentReady(true),
    [],
  );
  // The Activity flip below re-renders the whole ExploreContent tree
  // (hundreds of components). Deferring it keeps that work out of the urgent
  // commit that mounts the overlay and starts the open/close animations —
  // the overlay fully covers the content, so swapping it a frame later is
  // invisible.
  const deferredSearchOpen = useDeferredValue(isSearchOpen);
  const shouldRestoreSearchFocusRef = useRef(false);
  const searchInputRef = useRef<SearchTextBoxRef>(null);
  const flatListRef = useRef<FlatList>(null);
  const clearSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // The overlay outlives isSearchOpen by the fade-out duration so closing can
  // animate. Reanimated `exiting` presets are skipped entirely in some
  // release/new-arch combinations (component removed with no animation), so
  // the fade is driven by a shared value instead and unmount is delayed.
  const [isOverlayMounted, setIsOverlayMounted] = useState(false);
  const overlayProgress = useSharedValue(0);
  const overlayUnmountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Render-phase state adjustment so the overlay mounts in the same commit
  // that opens the search (an effect would mount it a frame late).
  if (isSearchOpen && !isOverlayMounted) {
    setIsOverlayMounted(true);
  }

  useEffect(() => {
    if (overlayUnmountTimeoutRef.current) {
      clearTimeout(overlayUnmountTimeoutRef.current);
      overlayUnmountTimeoutRef.current = null;
    }

    if (isSearchOpen) {
      overlayProgress.set(
        withTiming(1, {
          duration: SEARCH_OVERLAY_FADE_IN_MS,
          easing: Easing.out(Easing.quad),
        }),
      );
      return;
    }

    overlayProgress.set(
      withTiming(0, {
        duration: SEARCH_OVERLAY_FADE_OUT_MS,
        easing: Easing.in(Easing.quad),
      }),
    );
    overlayUnmountTimeoutRef.current = setTimeout(() => {
      overlayUnmountTimeoutRef.current = null;
      setIsOverlayMounted(false);
    }, SEARCH_OVERLAY_FADE_OUT_MS);
  }, [isSearchOpen, overlayProgress]);

  useEffect(
    () => () => {
      if (overlayUnmountTimeoutRef.current) {
        clearTimeout(overlayUnmountTimeoutRef.current);
      }
    },
    [],
  );

  const overlayAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: overlayProgress.value,
    }),
    [overlayProgress],
  );

  const isSearchActive = searchTerm.length > 0;
  const showSearchResults = isOverlayMounted && isSearchActive;
  const showRecentSearches = isOverlayMounted && !isSearchActive;
  const showSearchCancel = isSearchOpen;
  const recentSearches = useMemo(
    () => recentSearchesPages?.pages.flatMap((page) => page.data) ?? [],
    [recentSearchesPages],
  );

  useEffect(() => {
    flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
  }, [searchTerm]);

  const clearPendingSearchReset = useCallback(() => {
    if (clearSearchTimeoutRef.current) {
      clearTimeout(clearSearchTimeoutRef.current);
      clearSearchTimeoutRef.current = null;
    }
  }, []);

  const handleTabPress = useCallback(() => {
    clearPendingSearchReset();
    setSearchTermInput("").catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "search",
        operation: "clear-search-term",
      });
    });
    setIsSearchOpen(false);
    Keyboard.dismiss();
  }, [clearPendingSearchReset, setSearchTermInput]);

  const dismissSearch = useCallback(() => {
    Keyboard.dismiss();
  }, []);

  const cancelSearch = useCallback(() => {
    clearPendingSearchReset();
    triggerSearchCancelHaptic();
    setIsSearchOpen(false);
    requestAnimationFrame(dismissSearch);

    clearSearchTimeoutRef.current = setTimeout(() => {
      clearSearchTimeoutRef.current = null;
      setSearchTermInput("").catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "search",
          operation: "cancel-search",
        });
      });
    }, SEARCH_CANCEL_ANIMATION_DURATION_MS);
  }, [clearPendingSearchReset, dismissSearch, setSearchTermInput]);

  useEffect(() => clearPendingSearchReset, [clearPendingSearchReset]);

  const clearSearchText = useCallback(() => {
    clearPendingSearchReset();
    setIsSearchOpen(true);
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [clearPendingSearchReset]);

  const openSearch = useCallback(() => {
    clearPendingSearchReset();
    setIsSearchOpen(true);
  }, [clearPendingSearchReset]);

  const handleSearchInputChange = useCallback(
    (newValue: string) => {
      openSearch();
      setSearchTermInput(newValue).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "search",
          operation: "change-search-term",
        });
      });
    },
    [openSearch, setSearchTermInput],
  );

  const handleSearchSubmit = useCallback(() => {
    setIsSearchOpen(searchTerm.length > 0);
    Keyboard.dismiss();
  }, [searchTerm.length]);

  const handleSearchResultNavigateAway = useCallback(() => {
    shouldRestoreSearchFocusRef.current = true;
  }, []);

  const addRecentSearch = useCallback(
    (item: Pick<Search, "id" | "type">) => {
      addSearch.mutate(item);
    },
    [addSearch],
  );

  const removeRecentSearch = useCallback(
    (id: string) => {
      removeSearch.mutate(id);
    },
    [removeSearch],
  );

  useFocusEffect(
    useCallback(() => {
      if (!shouldRestoreSearchFocusRef.current || searchTerm.length === 0) {
        return;
      }

      shouldRestoreSearchFocusRef.current = false;
      setIsSearchOpen(true);

      const timeout = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);

      return () => clearTimeout(timeout);
    }, [searchTerm.length]),
  );

  useEffect(() => {
    const parent = navigation.getParent();
    if (!parent) return;
    return parent.addListener("tabPress" as never, handleTabPress as never);
  }, [navigation, handleTabPress]);

  // Publish which explore subgroup is active so the global scheduler
  // cancels the others when the user switches modes.
  useEffect(() => {
    if (showSearchResults) {
      setActiveImageGroup(EXPLORE_SEARCH_RESULTS_KEY);
    } else if (showRecentSearches) {
      setActiveImageGroup(EXPLORE_RECENT_SEARCHES_KEY);
    } else {
      setActiveImageGroup(EXPLORE_CONTENT_KEY);
    }
  }, [showSearchResults, showRecentSearches]);

  return (
    <LScreen enableAutomaticTopPadding={true} paddingHorizontal="zero" keyboard>
      <SentryScreenDisplay ready={isExploreContentReady} />
      <SearchHeader
        inputRef={searchInputRef}
        onBlur={dismissSearch}
        onCancel={cancelSearch}
        onChange={handleSearchInputChange}
        onClear={clearSearchText}
        onFocus={openSearch}
        onSubmitEditing={handleSearchSubmit}
        showCancel={showSearchCancel}
        value={searchTerm}
      />

      <View style={styles.contentFrame}>
        <View
          accessibilityElementsHidden={deferredSearchOpen}
          importantForAccessibility={
            deferredSearchOpen ? "no-hide-descendants" : "auto"
          }
          style={styles.exploreContentLayer}
        >
          <React.Activity
            mode={deferredSearchOpen ? "hidden" : "visible"}
            name="ExploreContent"
          >
            <ExploreContent onReady={markExploreContentReady} />
          </React.Activity>
        </View>

        {showRecentSearches && (
          <Animated.View
            pointerEvents={isSearchOpen ? "auto" : "none"}
            style={[styles.searchOverlay, overlayAnimatedStyle]}
          >
            <RecentSearchesPane
              fetchNextPage={fetchNextRecentSearchesPage}
              hasNextPage={hasNextRecentSearchesPage}
              isFetchingNextPage={isFetchingNextRecentSearchesPage}
              isLoading={isRecentSearchesLoading}
              onAddSearch={addRecentSearch}
              onRemoveSearch={removeRecentSearch}
              recentSearches={recentSearches}
              userId={firebaseUser?.uid}
            />
          </Animated.View>
        )}

        {showSearchResults && (
          <Animated.View
            pointerEvents={isSearchOpen ? "auto" : "none"}
            style={[styles.searchOverlay, overlayAnimatedStyle]}
          >
            <SearchResultsPane
              flatListRef={flatListRef}
              isSearching={isSearching}
              onNavigateAway={handleSearchResultNavigateAway}
              onSelectResult={recordSearchSelection}
              searchResults={searchResults}
            />
          </Animated.View>
        )}
      </View>
    </LScreen>
  );
};

const styles = StyleSheet.create({
  cancelContainer: {
    alignSelf: "stretch",
    overflow: "hidden",
  },
  cancelLabelContainer: {
    bottom: 0,
    position: "absolute",
    right: 0,
    top: 0,
    width: SEARCH_CANCEL_WIDTH,
  },
  cancelPressable: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  contentFrame: {
    flex: 1,
    position: "relative",
  },
  exploreScroll: {
    flex: 1,
    paddingTop: 20,
  },
  exploreContentLayer: {
    flex: 1,
  },
  searchInput: {
    flex: 1,
  },
  searchOverlay: {
    backgroundColor: theme.colors.screenBg,
    bottom: 0,
    elevation: 10,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  searchResultsList: {
    marginTop: theme.spacing.s1,
  },
});
